import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ddnsApi } from '../services/api';
import {
  DdnsJetonPostes,
  DdnsJetonPostesInput,
  DdnsModeEnrolement,
  DdnsPoste,
  DdnsServeur,
} from '../types';
import { useToast } from '../components/Toast';
import { formatDateCourte } from '../utils/desktopVersions';
import {
  AIDES_MODE,
  LIBELLES_MODE,
  TOLERANCE_DIRECT_JOURS,
  ajouterJours,
  dateAlger,
  formatHorodatage,
  formatVuIlYa,
  nomPoste,
  pastillePoste,
  usageDirect,
} from '../utils/ddns';

// Volet « Postes » d'un abonnement : les jetons d'enrôlement et les postes distants qu'ils ont fait
// entrer. Politique : docs/ddns/POLITIQUE_JETONS_POSTES.md. Un poste se présente une fois avec un
// jeton, reçoit un secret qui n'est qu'à lui, puis attend qu'on l'approuve ici. Révoquer un poste ou
// un jeton est définitif ; le serveur refait tous les contrôles.

interface Props {
  serveur: DdnsServeur;
  canEdit: boolean;
  /** Droit ddns:delete : supprimer un poste déjà coupé. */
  canDelete: boolean;
  onFermer: () => void;
  /** Un jeton, un poste ou un compteur a changé : la liste des abonnements se recharge. */
  onModifie: () => void;
  /** Jeton neuf, à montrer une seule fois (fenêtre du jeton de la page). */
  onJeton: (jeton: string, directJusquAu: string | null) => void;
}

interface FormJeton {
  id?: number;
  libelle: string;
  mode_enrolement: DdnsModeEnrolement;
  postes_max: string;
  expire_le: string;
  direct: boolean;
  direct_jusqu_au: string;
}

interface SaisieMotif {
  titre: string;
  explication: string;
  bouton: string;
  obligatoire: boolean;
  motif: string;
  executer: (motif: string) => Promise<unknown>;
  succes: string;
}

const LIBELLE_MAX = 60;
const MOTIF_MAX = 200;

// Le relais revérifie chaque poste connecté toutes les 120 s (étape 2 de la politique) : la page est
// déployée APRÈS lui. Si le relais revenait à une version sans revérification, revenir au texte
// « une session déjà ouverte par le relais reste ouverte jusqu’à la fermeture du logiciel ».
const COUPURE_RELAIS =
  'Une session déjà ouverte par le relais (accès sans box) est coupée dans les 2 minutes.';

const messageErreur = (err: any, defaut: string): string =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || defaut;

const DdnsPostes: React.FC<Props> = ({ serveur, canEdit, canDelete, onFermer, onModifie, onJeton }) => {
  const [jetons, setJetons] = useState<DdnsJetonPostes[]>([]);
  const [postes, setPostes] = useState<DdnsPoste[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toutVoir, setToutVoir] = useState(false);
  const [form, setForm] = useState<FormJeton | null>(null);
  const [saisie, setSaisie] = useState<SaisieMotif | null>(null);
  const [renommage, setRenommage] = useState<{ poste: DdnsPoste; libelle: string } | null>(null);
  const { showToast, showConfirm } = useToast();

  const charger = useCallback(async () => {
    try {
      const response = await ddnsApi.getJetonsPostes(serveur.id);
      setJetons(Array.isArray(response.data?.jetons) ? response.data.jetons : []);
      setPostes(Array.isArray(response.data?.postes) ? response.data.postes : []);
    } catch (err: any) {
      showToast(messageErreur(err, 'Chargement des postes impossible'), 'error');
    } finally {
      setLoading(false);
    }
  }, [serveur.id, showToast]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Après chaque action : le volet et la liste des abonnements (compteurs) se rechargent.
  const agir = async (action: () => Promise<unknown>, succes: string): Promise<boolean> => {
    try {
      setBusy(true);
      await action();
      showToast(succes, 'success');
      return true;
    } catch (err: any) {
      showToast(messageErreur(err, 'Action impossible'), 'error');
      return false;
    } finally {
      setBusy(false);
      await charger();
      onModifie();
    }
  };

  const enAttente = postes.filter((poste) => pastillePoste(poste, jetons).code === 'attente');
  const postesVisibles = useMemo(
    () => (toutVoir ? postes : postes.filter((poste) => pastillePoste(poste, jetons).code !== 'coupe')),
    [postes, jetons, toutVoir]
  );
  const jetonsVisibles = toutVoir ? jetons : jetons.filter((jeton) => jeton.etat !== 'REVOQUE');
  const masques = postes.length - postesVisibles.length + jetons.length - jetonsVisibles.length;

  // ---- Jetons -------------------------------------------------------------------------------
  const ouvrirCreation = () =>
    setForm({
      libelle: '',
      mode_enrolement: 'APPROBATION',
      postes_max: '',
      expire_le: '',
      // Tant que les logiciels des postes ne savent pas s'enrôler, le jeton ne sert qu'en direct :
      // la tolérance est donc proposée d'office, au plus loin permis.
      direct: true,
      direct_jusqu_au: ajouterJours(dateAlger(), TOLERANCE_DIRECT_JOURS),
    });

  const ouvrirModification = (jeton: DdnsJetonPostes) =>
    setForm({
      id: jeton.id,
      libelle: jeton.libelle,
      mode_enrolement: jeton.mode_enrolement,
      postes_max: jeton.postes_max === null ? '' : String(jeton.postes_max),
      expire_le: jeton.expire_le || '',
      direct: jeton.direct_jusqu_au !== null,
      direct_jusqu_au: jeton.direct_jusqu_au || ajouterJours(dateAlger(), TOLERANCE_DIRECT_JOURS),
    });

  const limiteDirect = ajouterJours(dateAlger(), TOLERANCE_DIRECT_JOURS);
  const erreursForm = useMemo(() => {
    if (!form) {
      return { libelle: '', plafond: '', direct: '', valide: false };
    }
    const libelle = form.libelle.trim() === '' ? 'Libellé obligatoire'
      : form.libelle.trim().length > LIBELLE_MAX ? `${LIBELLE_MAX} caractères au plus` : '';
    const plafond = form.postes_max !== '' &&
      !(/^\d+$/.test(form.postes_max) && Number(form.postes_max) >= 1 && Number(form.postes_max) <= 1000)
      ? 'Nombre entier de 1 à 1 000, ou vide (sans plafond)' : '';
    let direct = '';
    if (form.direct) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.direct_jusqu_au)) {
        direct = 'Date obligatoire';
      } else if (form.direct_jusqu_au > limiteDirect) {
        direct = `${TOLERANCE_DIRECT_JOURS} jours au plus : jusqu’au ${formatDateCourte(limiteDirect)}`;
      }
    }
    return { libelle, plafond, direct, valide: !libelle && !plafond && !direct };
  }, [form, limiteDirect]);

  const enregistrerJeton = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || !erreursForm.valide) {
      return;
    }
    const payload: DdnsJetonPostesInput = {
      libelle: form.libelle.trim(),
      mode_enrolement: form.mode_enrolement,
      postes_max: form.postes_max === '' ? null : Number(form.postes_max),
      expire_le: form.expire_le || null,
      direct_jusqu_au: form.direct ? form.direct_jusqu_au : null,
    };
    if (form.id) {
      const id = form.id;
      if (await agir(() => ddnsApi.updateJetonPostes(id, payload), `Jeton « ${payload.libelle} » modifié`)) {
        setForm(null);
      }
      return;
    }
    try {
      setBusy(true);
      const response = await ddnsApi.createJetonPostes(serveur.id, payload);
      setForm(null);
      showToast(`Jeton « ${payload.libelle} » créé`, 'success');
      if (response.data?.jeton) {
        onJeton(response.data.jeton, payload.direct_jusqu_au);
      }
    } catch (err: any) {
      showToast(messageErreur(err, 'Création impossible'), 'error');
    } finally {
      setBusy(false);
      await charger();
      onModifie();
    }
  };

  const revoquerJeton = (jeton: DdnsJetonPostes) =>
    setSaisie({
      titre: `Révoquer le jeton « ${jeton.libelle} »`,
      explication:
        `Définitif. Le jeton ne fait plus entrer aucun poste, et ses ${jeton.postes_autorises} poste(s) ` +
        'autorisé(s) n’obtiennent plus l’adresse du serveur, dès leur prochaine demande (au démarrage du ' +
        `logiciel). ${COUPURE_RELAIS} Pour les garder, enrôlez-les avec un autre jeton.`,
      bouton: 'Révoquer le jeton',
      obligatoire: true,
      motif: '',
      executer: (motif) => ddnsApi.revokeJetonPostes(jeton.id, motif),
      succes: `Jeton « ${jeton.libelle} » révoqué`,
    });

  // Jeton révoqué seulement, avec ses postes : la révocation les a déjà tous coupés.
  const supprimerJeton = (jeton: DdnsJetonPostes) => {
    const nombre = postes.filter((poste) => poste.id_jeton === jeton.id).length;
    showConfirm(
      `Supprimer définitivement le jeton « ${jeton.libelle} » de la liste ?` +
        (nombre > 0
          ? ` Ses ${nombre} poste${nombre > 1 ? 's' : ''}, déjà coupé${nombre > 1 ? 's' : ''} par la révocation, ` +
            `ser${nombre > 1 ? 'ont supprimés' : 'a supprimé'} avec lui.`
          : '') +
        ' Rien ne change pour l’accès : un poste qui présente encore ce jeton ou son secret reste refusé.',
      () => {
        void agir(() => ddnsApi.removeJetonPostes(jeton.id), `Jeton « ${jeton.libelle} » supprimé`);
      },
      'Supprimer le jeton'
    );
  };

  // ---- Postes -------------------------------------------------------------------------------
  const approuver = (poste: DdnsPoste) =>
    agir(() => ddnsApi.approvePoste(poste.id), `${nomPoste(poste)} autorisé`);

  const refuser = (poste: DdnsPoste) =>
    setSaisie({
      titre: `Refuser la demande de ${nomPoste(poste)}`,
      explication: 'Le poste n’obtiendra pas l’adresse. S’il se présente de nouveau, ce sera une nouvelle demande.',
      bouton: 'Refuser',
      obligatoire: false,
      motif: '',
      executer: (motif) => ddnsApi.refusePoste(poste.id, motif),
      succes: `Demande de ${nomPoste(poste)} refusée`,
    });

  const revoquerPoste = (poste: DdnsPoste) =>
    setSaisie({
      titre: `Révoquer ${nomPoste(poste)}`,
      explication:
        'Définitif : ce poste n’obtient plus l’adresse du serveur, dès sa prochaine demande (au démarrage du ' +
        `logiciel). ${COUPURE_RELAIS} Pour le reprendre un jour, il devra se présenter de nouveau et être approuvé.`,
      bouton: 'Révoquer le poste',
      obligatoire: true,
      motif: '',
      executer: (motif) => ddnsApi.revokePoste(poste.id, motif),
      succes: `${nomPoste(poste)} révoqué`,
    });

  // Poste déjà coupé seulement : l'effacer ne change rien à son accès. S'il se présente encore avec
  // son secret, il est traité en inconnu ; pour revenir, il doit s'enrôler de nouveau.
  const supprimerPoste = (poste: DdnsPoste) =>
    showConfirm(
      `Supprimer définitivement ${nomPoste(poste)} (code ${poste.code}) de la liste ? Il est déjà coupé : ` +
        'son accès ne change pas. S’il se présente encore, il sera traité comme un poste inconnu ; pour ' +
        'revenir, il devra s’enrôler de nouveau avec un jeton et être approuvé.',
      () => {
        void agir(() => ddnsApi.removePoste(poste.id), `${nomPoste(poste)} supprimé`);
      },
      'Supprimer le poste'
    );

  const validerSaisie = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!saisie || (saisie.obligatoire && saisie.motif.trim() === '')) {
      return;
    }
    const courante = saisie;
    if (await agir(() => courante.executer(courante.motif.trim()), courante.succes)) {
      setSaisie(null);
    }
  };

  const validerRenommage = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!renommage || renommage.libelle.trim().length > LIBELLE_MAX) {
      return;
    }
    const { poste, libelle } = renommage;
    if (await agir(() => ddnsApi.renamePoste(poste.id, libelle.trim() || null), 'Poste renommé')) {
      setRenommage(null);
    }
  };

  const fenetreSecondaire = form !== null || saisie !== null || renommage !== null;

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal modal-wide ad-postes" role="dialog" aria-modal="true" aria-labelledby="ad-postes-titre"
        aria-hidden={fenetreSecondaire}>
        <div className="ad-postes-entete">
          <h2 id="ad-postes-titre">
            Postes : <span className="ad-mono">{serveur.nom_serveur}</span>
          </h2>
          <button type="button" className="btn btn-secondary" onClick={onFermer}>Fermer</button>
        </div>
        <p className="ad-subtitle">{serveur.client_name}</p>

        <details className="ad-aide">
          <summary>Comment ça marche</summary>
          <ul>
            <li>
              Un <strong>jeton</strong> fait entrer des postes : collé sur un poste, celui-ci se présente une fois et
              reçoit un secret qui n&apos;est qu&apos;à lui. Le jeton peut ensuite être effacé du poste.
            </li>
            <li>
              Sur approbation, chaque nouveau poste apparaît ici <strong>en attente</strong> avec son code (8
              caractères, affiché sur le poste) : comparez-le avant d&apos;approuver.
            </li>
            <li>
              <strong>Révoquer</strong> un poste ou un jeton est définitif : le poste n&apos;obtient plus
              l&apos;adresse dès sa prochaine demande. {COUPURE_RELAIS}
            </li>
            <li>
              <strong>Supprimer</strong> un poste coupé (révoqué, refusé ou jeton révoqué), ou un jeton révoqué avec
              ses postes, l&apos;efface de la liste sans rien changer à l&apos;accès. Pour revenir, un poste devra
              s&apos;enrôler de nouveau.
            </li>
            <li>
              <strong>Usage direct</strong> : les logiciels pas encore mis à jour présentent le jeton à chaque
              démarrage, sans s&apos;enrôler. C&apos;est toléré {TOLERANCE_DIRECT_JOURS} jours au plus, le temps de
              mettre les postes à jour.
            </li>
          </ul>
        </details>

        {loading ? (
          <p className="ad-info">Chargement…</p>
        ) : (
          <>
            {enAttente.length > 0 && (
              <p className="ad-alerte" role="status">
                <strong>{enAttente.length} poste{enAttente.length > 1 ? 's' : ''} en attente</strong> de votre
                décision.
              </p>
            )}

            <div className="ad-postes-section">
              <h3>Jetons</h3>
              {canEdit && (
                <button type="button" className="btn btn-primary" onClick={ouvrirCreation} disabled={busy}>
                  Nouveau jeton
                </button>
              )}
            </div>
            <div className="table-container">
              <table className="ad-table ad-table-compacte">
                <thead>
                  <tr>
                    <th>Jeton</th>
                    <th>Entrée des postes</th>
                    <th>Postes</th>
                    <th>Usage direct</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jetonsVisibles.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">
                        Aucun jeton en vigueur.{canEdit && ' « Nouveau jeton » pour faire entrer des postes.'}
                      </td>
                    </tr>
                  )}
                  {jetonsVisibles.map((jeton) => (
                    <tr key={jeton.id} className={jeton.etat === 'REVOQUE' ? 'ad-ligne-suspendue' : ''}>
                      <td>
                        <div className="ad-client">{jeton.libelle}</div>
                        <div className="ad-secondaire">
                          créé le {formatHorodatage(jeton.cree_le)}{jeton.cree_par ? ` par ${jeton.cree_par}` : ''}
                        </div>
                        {jeton.etat === 'REVOQUE' && (
                          <div className="ad-secondaire">
                            révoqué le {formatHorodatage(jeton.revoque_le)}
                            {jeton.revoque_par ? ` par ${jeton.revoque_par}` : ''}
                            {jeton.motif_revocation ? ` : ${jeton.motif_revocation}` : ''}
                          </div>
                        )}
                      </td>
                      <td>
                        {jeton.etat === 'ACTIF' && <div>{LIBELLES_MODE[jeton.mode_enrolement]}</div>}
                        {jeton.etat === 'ECHU' && <span className="ad-pastille ad-expiration-expire">Échu</span>}
                        {jeton.etat === 'REVOQUE' && <span className="ad-pastille ad-expiration-suspendu">Révoqué</span>}
                        {jeton.expire_le && (
                          <div className="ad-secondaire">
                            {jeton.etat === 'ECHU' ? 'échu le' : 'jusqu’au'} {formatDateCourte(jeton.expire_le)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div>
                          {jeton.postes_autorises}
                          {jeton.postes_max !== null ? ` / ${jeton.postes_max}` : ''} autorisé
                          {jeton.postes_autorises > 1 ? 's' : ''}
                        </div>
                        {jeton.postes_en_attente > 0 && (
                          <div className="ad-secondaire">{jeton.postes_en_attente} en attente</div>
                        )}
                      </td>
                      <td>
                        <div>{usageDirect(jeton)}</div>
                        {jeton.direct_jusqu_au && (
                          <div className="ad-secondaire">
                            {jeton.derniere_utilisation
                              ? `dernier usage ${formatVuIlYa(jeton.derniere_utilisation)}`
                              : 'jamais utilisé'}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="ad-actions">
                          {canEdit && jeton.etat !== 'REVOQUE' && (
                            <>
                              <button className="btn btn-secondary" disabled={busy} onClick={() => ouvrirModification(jeton)}
                                aria-label={`Modifier le jeton ${jeton.libelle}`}>
                                Modifier
                              </button>
                              <button className="btn btn-secondary ad-btn-danger" disabled={busy}
                                onClick={() => revoquerJeton(jeton)} aria-label={`Révoquer le jeton ${jeton.libelle}`}>
                                Révoquer
                              </button>
                            </>
                          )}
                          {canDelete && jeton.etat === 'REVOQUE' && (
                            <button className="btn btn-secondary ad-btn-danger" disabled={busy}
                              onClick={() => supprimerJeton(jeton)} aria-label={`Supprimer le jeton ${jeton.libelle}`}>
                              Supprimer
                            </button>
                          )}
                          {(jeton.etat === 'REVOQUE' ? !canDelete : !canEdit) && '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="ad-postes-section">
              <h3>Postes</h3>
              {masques > 0 || toutVoir ? (
                <label className="ad-case">
                  <input type="checkbox" checked={toutVoir} onChange={(event) => setToutVoir(event.target.checked)} />
                  Voir aussi les postes coupés et les jetons révoqués{!toutVoir && ` (${masques})`}
                </label>
              ) : null}
            </div>
            <div className="table-container">
              <table className="ad-table ad-table-compacte">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Logiciel</th>
                    <th>Statut</th>
                    <th>Dernière demande</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {postesVisibles.length === 0 && (
                    <tr>
                      <td colSpan={5} className="empty">
                        Aucun poste. Ils apparaissent ici quand ils se présentent avec un jeton.
                      </td>
                    </tr>
                  )}
                  {postesVisibles.map((poste) => {
                    const pastille = pastillePoste(poste, jetons);
                    return (
                      <tr key={poste.id} className={pastille.code === 'coupe' ? 'ad-ligne-suspendue' : ''}>
                        <td>
                          <div className="ad-client">{nomPoste(poste)}</div>
                          <div className="ad-secondaire">
                            code <span className="ad-mono">{poste.code}</span>
                            {poste.libelle && poste.nom_machine ? ` · ${poste.nom_machine}` : ''}
                          </div>
                          <div className="ad-secondaire" title={`Matériel : ${poste.hardware_id}`}>
                            jeton « {poste.jeton_libelle} » · demandé le {formatHorodatage(poste.enrole_le)}
                          </div>
                          {poste.doublons_materiel > 0 && (
                            <div className="ad-field-error">
                              Même matériel déclaré que {poste.doublons_materiel} autre{poste.doublons_materiel > 1 ? 's' : ''}{' '}
                              poste{poste.doublons_materiel > 1 ? 's' : ''} : poste réinstallé, ou copie du jeton ?
                            </div>
                          )}
                        </td>
                        <td>
                          <div>{poste.produit}</div>
                          {poste.app_version && <div className="ad-secondaire">version {poste.app_version}</div>}
                        </td>
                        <td>
                          <span className={`ad-pastille ad-poste-${pastille.code}`}>{pastille.libelle}</span>
                          {poste.decide_le && (
                            <div className="ad-secondaire">
                              le {formatHorodatage(poste.decide_le)}{poste.decide_par ? ` par ${poste.decide_par}` : ''}
                            </div>
                          )}
                          {poste.motif_decision && <div className="ad-secondaire">{poste.motif_decision}</div>}
                        </td>
                        <td>
                          <div title={formatHorodatage(poste.vu_le)}>
                            {poste.vu_le ? formatVuIlYa(poste.vu_le) : 'jamais'}
                          </div>
                          <div className="ad-secondaire ad-mono">{poste.derniere_ip || poste.ip_enrolement}</div>
                        </td>
                        <td>
                          <div className="ad-actions">
                            {canEdit && pastille.code === 'attente' && (
                              <>
                                <button className="btn btn-success" disabled={busy} onClick={() => approuver(poste)}
                                  aria-label={`Approuver ${nomPoste(poste)}`}>
                                  Approuver
                                </button>
                                <button className="btn btn-secondary" disabled={busy} onClick={() => refuser(poste)}
                                  aria-label={`Refuser ${nomPoste(poste)}`}>
                                  Refuser
                                </button>
                              </>
                            )}
                            {canEdit && (poste.statut === 'AUTORISE' || poste.statut === 'EN_ATTENTE') && (
                              <button className="btn btn-secondary ad-btn-danger" disabled={busy}
                                onClick={() => revoquerPoste(poste)} aria-label={`Révoquer ${nomPoste(poste)}`}>
                                Révoquer
                              </button>
                            )}
                            {canEdit && (
                              <button className="btn btn-secondary" disabled={busy}
                                onClick={() => setRenommage({ poste, libelle: poste.libelle || '' })}
                                aria-label={`Renommer ${nomPoste(poste)}`}>
                                Renommer
                              </button>
                            )}
                            {canDelete && pastille.code === 'coupe' && (
                              <button className="btn btn-secondary ad-btn-danger" disabled={busy}
                                onClick={() => supprimerPoste(poste)} aria-label={`Supprimer ${nomPoste(poste)}`}>
                                Supprimer
                              </button>
                            )}
                            {!canEdit && !(canDelete && pastille.code === 'coupe') && '-'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {form && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ad-form-jeton-titre">
            <h2 id="ad-form-jeton-titre">{form.id ? 'Modifier le jeton' : 'Nouveau jeton'}</h2>
            {!form.id && (
              <p className="ad-info">Le jeton est généré à l&apos;enregistrement et <strong>affiché une seule fois</strong>.</p>
            )}
            <form onSubmit={enregistrerJeton} noValidate>
              <div className="form-group">
                <label htmlFor="ad-jeton-libelle">Libellé</label>
                <input id="ad-jeton-libelle" value={form.libelle} maxLength={LIBELLE_MAX} placeholder="ex. Agence Sétif"
                  onChange={(event) => setForm({ ...form, libelle: event.target.value })}
                  aria-invalid={Boolean(erreursForm.libelle) && form.libelle !== ''} />
                {erreursForm.libelle && form.libelle !== '' && <p className="ad-field-error">{erreursForm.libelle}</p>}
              </div>
              <div className="form-group">
                <label htmlFor="ad-jeton-mode">Entrée des nouveaux postes</label>
                <select id="ad-jeton-mode" value={form.mode_enrolement}
                  onChange={(event) => setForm({ ...form, mode_enrolement: event.target.value as DdnsModeEnrolement })}>
                  {(Object.keys(LIBELLES_MODE) as DdnsModeEnrolement[]).map((mode) => (
                    <option key={mode} value={mode}>{LIBELLES_MODE[mode]}</option>
                  ))}
                </select>
                <p className="ad-field-hint">{AIDES_MODE[form.mode_enrolement]}</p>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="ad-jeton-plafond">Postes autorisés au plus</label>
                  <input id="ad-jeton-plafond" inputMode="numeric" value={form.postes_max} placeholder="sans plafond"
                    onChange={(event) => setForm({ ...form, postes_max: event.target.value.trim() })}
                    aria-invalid={Boolean(erreursForm.plafond)} />
                </div>
                <div className="form-group">
                  <label htmlFor="ad-jeton-echeance">Fait entrer des postes jusqu&apos;au</label>
                  <input id="ad-jeton-echeance" type="date" value={form.expire_le}
                    onChange={(event) => setForm({ ...form, expire_le: event.target.value })} />
                </div>
              </div>
              {erreursForm.plafond && <p className="ad-field-error">{erreursForm.plafond}</p>}
              <p className="ad-field-hint">
                Sans date : sans échéance. Passée l&apos;échéance, les postes déjà autorisés continuent.
              </p>
              <div className="form-group">
                <label className="ad-case">
                  <input type="checkbox" checked={form.direct}
                    onChange={(event) => setForm({ ...form, direct: event.target.checked })} />
                  Accepter aussi ce jeton en usage direct (logiciels pas encore mis à jour)
                </label>
                {form.direct && (
                  <>
                    <input id="ad-jeton-direct" type="date" aria-label="Usage direct jusqu’au" value={form.direct_jusqu_au}
                      max={limiteDirect} onChange={(event) => setForm({ ...form, direct_jusqu_au: event.target.value })} />
                    <p className={erreursForm.direct ? 'ad-field-error' : 'ad-field-hint'}>
                      {erreursForm.direct ||
                        `Jusqu’au ${formatDateCourte(limiteDirect)} au plus (${TOLERANCE_DIRECT_JOURS} jours), jour inclus.`}
                    </p>
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setForm(null)} disabled={busy}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={!erreursForm.valide || busy}>
                  {busy ? 'Enregistrement…' : form.id ? 'Enregistrer' : 'Créer le jeton'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {saisie && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ad-saisie-titre">
            <h2 id="ad-saisie-titre">{saisie.titre}</h2>
            <p className="ad-alerte">{saisie.explication}</p>
            <form onSubmit={validerSaisie} noValidate>
              <div className="form-group">
                <label htmlFor="ad-saisie-motif">Motif{saisie.obligatoire ? '' : ' (facultatif)'}</label>
                <input id="ad-saisie-motif" value={saisie.motif} maxLength={MOTIF_MAX} autoFocus
                  placeholder="ex. Ordinateur remplacé" onChange={(event) => setSaisie({ ...saisie, motif: event.target.value })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setSaisie(null)} disabled={busy}>
                  Annuler
                </button>
                <button type="submit" className="btn ad-btn-revoquer"
                  disabled={busy || (saisie.obligatoire && saisie.motif.trim() === '')}>
                  {saisie.bouton}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {renommage && (
        <div className="modal-overlay" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ad-renommer-titre">
            <h2 id="ad-renommer-titre">Renommer le poste <span className="ad-mono">{renommage.poste.code}</span></h2>
            <form onSubmit={validerRenommage} noValidate>
              <div className="form-group">
                <label htmlFor="ad-renommer">Nom du poste</label>
                <input id="ad-renommer" value={renommage.libelle} maxLength={LIBELLE_MAX} autoFocus
                  placeholder={renommage.poste.nom_machine || 'ex. Comptabilité'}
                  onChange={(event) => setRenommage({ ...renommage, libelle: event.target.value })} />
                <p className="ad-field-hint">Vide : le nom de la machine est affiché.</p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setRenommage(null)} disabled={busy}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DdnsPostes;
